/**
 * Dernière étape de l'onboarding ÉLÈVE (migration 0320) : sur le MÊME écran
 * que les objectifs, en premier, on propose de faire le diagnostic de
 * programme tout de suite (choix ludique « Oui, maintenant » / « Plus tard »).
 * La validation (bouton fixé en bas, dans l'onboarding) lance alors
 * `/student/diagnostic?start=1`. Rien n'est affiché si aucun diagnostic n'est
 * possible (pas encore de SKILLCARDS pour son niveau).
 */

import { Card, CardContent, useDelayedReveal, usePrefersReducedMotion } from '@jeraisonne/ui';
import { OnboardingSteps } from '@/features/onboarding/onboarding-ui';
import { onboardingStepLabels } from '@/lib/onboarding-flow';

/** Étapes du parcours élève, pour la barre de progression de l'onboarding. */
export const STUDENT_ONBOARDING_STEPS = onboardingStepLabels('student');

export function OnboardingProgress({ current }: { current: number }) {
  return <OnboardingSteps labels={STUDENT_ONBOARDING_STEPS} current={current} />;
}

const PERKS = [
  { emoji: '⏱️', text: '5 minutes, une quinzaine de questions' },
  { emoji: '⚡', text: 'De l’XP à chaque bonne réponse' },
  { emoji: '🃏', text: 'On repère les SKILLCARDS à retravailler' },
  { emoji: '⏸️', text: 'Tu t’arrêtes et tu reprends quand tu veux' },
];

/** Libellé du bouton de validation (fixé en bas) de l'étape objectifs + diagnostic. */
export function onboardingFinishLabel(diagnosticNow: boolean, objectiveCount: number): string {
  if (diagnosticNow) return 'Valider et lancer mon diagnostic 🚀';
  if (objectiveCount === 1) return 'Valider mon objectif';
  if (objectiveCount > 1) return `Valider mes ${objectiveCount} objectifs`;
  return 'Aller à mon tableau de bord';
}

export function OnboardingDiagnosticChoice({
  value,
  onChange,
}: {
  /** `true` = faire le diagnostic dès la validation. */
  value: boolean;
  onChange: (now: boolean) => void;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const revealed = useDelayedReveal({
    key: 'onboarding-diagnostic',
    delayMs: 150,
    immediate: reducedMotion,
  });

  return (
    <Card className="border-primary-200 overflow-hidden shadow-lg">
      <CardContent className="space-y-4 py-5">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className={`bg-primary-50 ocean:bg-white/10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-3xl ${
              revealed ? 'animate-scale-in' : 'opacity-0'
            }`}
          >
            🧭
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-ink-900 text-lg font-extrabold">
              On fait ton diagnostic ?
            </h2>
            <p className="text-ink-600 text-sm">
              On fait le point sur ce que tu sais déjà, pour te proposer pile ce qu’il te faut.
            </p>
          </div>
        </div>

        <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {PERKS.map((p, i) => (
            <li
              key={p.text}
              style={{ animationDelay: `${150 + i * 90}ms` }}
              className={`text-ink-700 flex items-center gap-2 text-sm ${
                revealed ? 'animate-scale-in [animation-fill-mode:both]' : 'opacity-0'
              }`}
            >
              <span aria-hidden className="text-lg leading-none">
                {p.emoji}
              </span>
              {p.text}
            </li>
          ))}
        </ul>

        <div role="radiogroup" aria-label="Faire le diagnostic" className="grid grid-cols-2 gap-2">
          <ChoiceButton selected={value} onClick={() => onChange(true)}>
            Oui, maintenant
          </ChoiceButton>
          <ChoiceButton selected={!value} onClick={() => onChange(false)}>
            Plus tard
          </ChoiceButton>
        </div>
        {!value && (
          <p className="text-ink-500 text-xs">
            Pas de souci : il t’attend sur ton tableau de bord.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ChoiceButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={`min-h-[2.75rem] rounded-xl border-2 px-3 text-sm font-semibold transition-colors active:scale-[0.98] ${
        selected
          ? 'border-primary-500 bg-primary-50 text-primary-700 ocean:bg-primary-500/15'
          : 'border-ink-100 text-ink-600'
      }`}
    >
      {children}
    </button>
  );
}
